import { useEffect, useState } from "react";
import DashboardLayout from "../../layouts/DashboardLayout";
import { getMembers } from "../../services/memberService";
import "./RenewalNotifications.css";

export default function RenewalNotifications() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  // ==========================================
  // GET DAYS LEFT
  // ==========================================

  const getDaysLeft = (expiryDate) => {
    if (!expiryDate) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);

    return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  };

  // ==========================================
  // LOAD MEMBERS
  // ==========================================

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    try {
      setLoading(true);

      const members = await getMembers();

      const data = members
        .filter((member) => {
          if (!member.expiryDate) return false;

          const daysLeft = getDaysLeft(member.expiryDate);

          // Show expired + next 7 days
          return daysLeft <= 7;
        })
        .sort((a, b) => {
          return getDaysLeft(a.expiryDate) - getDaysLeft(b.expiryDate);
        });

      setAlerts(data);
    } catch (error) {
      console.error("Renewal notification error:", error);
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // STATISTICS
  // ==========================================

  const totalAlerts = alerts.length;

  const expiringSoon = alerts.filter((member) => {
    const daysLeft = getDaysLeft(member.expiryDate);

    return daysLeft > 0 && daysLeft <= 7;
  }).length;

  const critical = alerts.filter((member) => {
    const daysLeft = getDaysLeft(member.expiryDate);

    return daysLeft > 0 && daysLeft <= 3;
  }).length;

  const expired = alerts.filter((member) => {
    const daysLeft = getDaysLeft(member.expiryDate);

    return daysLeft <= 0;
  }).length;

  return (
    <DashboardLayout>
      <div className="renewal-page">
        {/* ======================================
            HEADER
        ====================================== */}

        <div className="renewal-header">
          <div>
            <h1>Renewal Notifications</h1>

            <p
              style={{
                fontWeight: "bold",
                color: "white",
              }}
            >
              Monitor memberships that are expiring soon and take action before
              expiration.
            </p>
          </div>
        </div>

        {/* ======================================
            STATISTICS
        ====================================== */}

        <div className="renewal-stats">
          <div className="renewal-stat-card">
            <h4>Total Alerts</h4>
            <h2>{totalAlerts}</h2>
          </div>

          <div className="renewal-stat-card warning">
            <h4>Expiring Soon</h4>
            <h2>{expiringSoon}</h2>
          </div>

          <div className="renewal-stat-card danger">
            <h4>Expired</h4>
            <h2>{expired}</h2>
          </div>

          <div className="renewal-stat-card critical">
            <h4>Critical</h4>
            <h2>{critical}</h2>
          </div>
        </div>

        {/* ======================================
            TABLE
        ====================================== */}

        <div className="renewal-table-card">
          <table className="member-table">
            <thead>
              <tr>
                <th>Photo</th>
                <th>Member ID</th>
                <th>Member</th>
                <th>Plan</th>
                <th>Expiry Date</th>
                <th>Days Left</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="7"
                    style={{
                      textAlign: "center",
                    }}
                  >
                    Loading renewal notifications...
                  </td>
                </tr>
              ) : alerts.length > 0 ? (
                alerts.map((member) => {
                  const daysLeft = getDaysLeft(member.expiryDate);

                  return (
                    <tr key={member.id}>
                      {/* PHOTO */}

                      <td>
                        {member.photo ? (
                          <img
                            src={member.photo}
                            alt={member.fullName}
                            className="renewal-member-photo"
                          />
                        ) : member.photoBase64 ? (
                          <img
                            src={member.photoBase64}
                            alt={member.fullName}
                            className="renewal-member-photo"
                          />
                        ) : (
                          <div className="renewal-photo-placeholder">
                            {member.fullName?.charAt(0)?.toUpperCase() || "?"}
                          </div>
                        )}
                      </td>

                      {/* MEMBER ID */}

                      <td>
                        <strong className="member-id">
                          {member.memberId || "-"}
                        </strong>
                      </td>

                      {/* MEMBER NAME */}

                      <td>{member.fullName || "-"}</td>

                      {/* PLAN */}

                      <td>
                        <span className="plan-pill">
                          {member.membershipPlan || "-"}
                        </span>
                      </td>

                      {/* EXPIRY */}

                      <td>{member.expiryDate || "-"}</td>

                      {/* DAYS LEFT */}

                      <td>
                        {daysLeft <= 0 ? (
                          <span className="days-expired">Expired</span>
                        ) : (
                          <span
                            className={
                              daysLeft <= 3 ? "days-critical" : "days-warning"
                            }
                          >
                            {daysLeft} {daysLeft === 1 ? "Day" : "Days"}
                          </span>
                        )}
                      </td>

                      {/* STATUS */}

                      <td>
                        {daysLeft <= 0 ? (
                          <span className="status-expired">Expired</span>
                        ) : daysLeft <= 3 ? (
                          <span className="status-critical">Critical</span>
                        ) : (
                          <span className="status-warning">Expiring Soon</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    style={{
                      textAlign: "center",
                    }}
                  >
                    No renewal notifications found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
